import React from "react";
import { ArrowRight, ChevronLeft, ChevronRight, ChevronRight as ArrowR, Sofa, CreditCard, Shield, Wrench } from "lucide-react";
import { useModals } from "@/lib/modal-context";

const SERVICES = [
    "Full home interiors",
    "Apartment interiors",
    "Villa interiors",
    "Modular kitchens",
    "Wardrobes",
    "Lighting",
    "Flooring",
    "Electrical work",
    "Civil work",
    "False ceiling",
    "Wall design & painting",
    "Furniture sourcing",
    "Final styling",
];

export default function WhatWeOffer() {
    const { openConnect } = useModals();
    return (
        <section id="about" className="bg-white py-16 sm:py-24">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
                <div className="rounded-[2rem] border border-black/5 bg-white p-6 sm:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                    {/* Header */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start fade-up">
                        <div className="lg:col-span-5">
                            <div className="text-[#7e9a00] text-xs font-bold tracking-[0.22em]">WHAT WE OFFER</div>
                            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                                Built with trust.
                                <br />
                                <span className="text-[#7e9a00]">Designed for Dubai homes.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-4 text-sm text-black/60 max-w-md mt-2">
                            From seamless service to honest pricing and lasting quality — everything we do is built around your home, your budget and your peace of mind.
                        </p>
                        <div className="lg:col-span-3 flex lg:justify-end">
                            <button
                                onClick={() => openConnect({ source: "free_quote" })}
                                data-testid="get-free-quote-btn"
                                className="btn-pill btn-lime pr-1.5"
                            >
                                <span className="px-1 text-sm font-bold">GET FREE QUOTE</span>
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                                    <ArrowRight className="h-4 w-4" />
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Cards */}
                    <div className="relative mt-12">
                        {/* arrows */}
                        <button className="hidden lg:flex absolute left-[-22px] top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-white border border-black/10 shadow-md hover:bg-[#CCFF00]" aria-label="prev">
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button className="hidden lg:flex absolute right-[-22px] top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-white border border-black/10 shadow-md hover:bg-[#CCFF00]" aria-label="next">
                            <ChevronRight className="h-5 w-5" />
                        </button>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            {/* Services */}
                            <div className="rounded-3xl bg-white border border-black/10 p-6 sm:p-8 fade-up">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F7F4DA]">
                                        <Sofa className="h-6 w-6 text-[#7e9a00]" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-xl font-bold">Our services</h3>
                                        <p className="mt-1 text-sm text-black/60 max-w-[16rem]">End-to-end solutions for every corner of your home.</p>
                                    </div>
                                </div>
                                <ul className="mt-6 divide-y divide-black/10">
                                    {SERVICES.map((s) => (
                                        <li key={s} className="flex items-center justify-between py-3.5 group cursor-pointer">
                                            <span className="flex items-center gap-3 text-sm font-semibold">
                                                <span className="h-2 w-2 rounded-full bg-[#7e9a00]" />
                                                {s}
                                            </span>
                                            <ArrowR className="h-4 w-4 text-black/40 group-hover:text-black group-hover:translate-x-1 transition" />
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Price benefits */}
                            <div className="rounded-3xl bg-white border border-black/10 p-6 sm:p-8 fade-up">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F7F4DA]">
                                        <CreditCard className="h-6 w-6 text-[#7e9a00]" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-xl font-bold">Price benefits</h3>
                                        <p className="mt-1 text-sm text-black/60 max-w-[16rem]">Honest pricing, flexible options and complete transparency.</p>
                                    </div>
                                </div>
                                <div className="mt-8 space-y-6">
                                    <div className="flex gap-4">
                                        <div className="h-11 w-11 shrink-0 rounded-xl bg-[#FFE3E3] flex items-center justify-center">
                                            <CreditCard className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="font-display text-base font-bold">Transparent quotations</div>
                                            <p className="mt-1 text-sm text-black/60">Clear, itemised pricing with flexible package options and budget-based material choices.</p>
                                        </div>
                                    </div>
                                    <div className="border-t border-black/10" />
                                    <div className="flex gap-4">
                                        <div className="h-11 w-11 shrink-0 rounded-xl bg-[#FFD6BC] flex items-center justify-center">
                                            <span className="text-lg">💰</span>
                                        </div>
                                        <div>
                                            <div className="font-display text-base font-bold">No surprise additions</div>
                                            <p className="mt-1 text-sm text-black/60">Any changes are approved by you first — no last-minute charges after execution begins.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Warranty */}
                            <div className="rounded-3xl bg-white border border-black/10 p-6 sm:p-8 fade-up">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F7F4DA]">
                                        <Shield className="h-6 w-6 text-[#7e9a00]" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-xl font-bold">Warranty</h3>
                                        <p className="mt-1 text-sm text-black/60 max-w-[16rem]">Long-term assurance for total peace of mind.</p>
                                    </div>
                                </div>
                                <div className="mt-8 space-y-6">
                                    <div className="flex gap-4">
                                        <div className="h-11 w-11 shrink-0 rounded-xl bg-[#E5DEFF] flex items-center justify-center">
                                            <Shield className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="font-display text-base font-bold">Vendor-backed material warranties</div>
                                            <p className="mt-1 text-sm text-black/60">Where applicable, materials come with manufacturer warranties passed on to you.</p>
                                        </div>
                                    </div>
                                    <div className="border-t border-black/10" />
                                    <div className="flex gap-4">
                                        <div className="h-11 w-11 shrink-0 rounded-xl bg-[#E5DEFF] flex items-center justify-center">
                                            <Wrench className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="font-display text-base font-bold">Post-handover support</div>
                                            <p className="mt-1 text-sm text-black/60">On-site coordination and support after handover for peace of mind.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
